/**
 * YayNotificationToggle — Header bar toggle for VOIP notifications
 *
 * Shows 🔔 Yay (enabled) or 🔕 Yay (muted).
 * Persists to WebUsers.YayNotificationsEnabled via PUT /api/yay/notifications.
 *
 * Props:
 *   - enabled: boolean (current state from parent)
 *   - onToggle(newValue): callback when toggled
 *   - isStaff: boolean — only render for staff
 *   - isDark: boolean
 */

import React, { useState, useCallback } from 'react';
import axios from 'axios';

export default function YayNotificationToggle({
  enabled = true,
  onToggle,
  isStaff = false,
  isDark = false,
}) {
  const [saving, setSaving] = useState(false);

  const handleClick = useCallback(async () => {
    if (saving) return;
    const newValue = !enabled;

    // Optimistic update
    if (onToggle) onToggle(newValue);

    setSaving(true);
    try {
      await axios.put('/api/yay/notifications', { enabled: newValue });
    } catch (err) {
      console.error('Failed to toggle Yay notifications:', err);
      // Revert on failure
      if (onToggle) onToggle(!newValue);
    } finally {
      setSaving(false);
    }
  }, [enabled, onToggle, saving]);

  if (!isStaff) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={saving}
      title={enabled ? 'Mute VOIP notifications' : 'Enable VOIP notifications'}
      style={{
        background: 'none',
        border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
        borderRadius: 6,
        padding: '4px 10px',
        cursor: saving ? 'wait' : 'pointer',
        fontSize: 13,
        color: isDark ? '#e2e8f0' : '#1e293b',
        opacity: saving ? 0.5 : 1,
        transition: 'opacity 0.2s',
        whiteSpace: 'nowrap',
      }}
    >
      {enabled ? '🔔' : '🔕'} Yay
    </button>
  );
}
